import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  CalendarDays,
  FileText,
  User,
  Banknote,
} from 'lucide-react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { db } from '../../config/firebase';
import useWalletData from '../../hooks/useWalletData';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';

const formatCurrency = value =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value || 0));

const formatDate = (timestamp, t) => {
  if (!timestamp) return t('common.notAvailable');
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getTypeStyles = (type, t) => {
  switch (type) {
    case 'deposit':
      return {
        icon: ArrowUpRight,
        classes:
          'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
        text: t('wallet.transactionDetail.type.deposit'),
      };
    case 'withdrawal':
      return {
        icon: ArrowDownRight,
        classes:
          'bg-rose-100 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400',
        text: t('wallet.transactionDetail.type.withdrawal'),
      };
    case 'earning':
      return {
        icon: ArrowUpRight,
        classes:
          'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400',
        text: t('wallet.transactionDetail.type.earning'),
      };
    default:
      return {
        icon: CreditCard,
        classes:
          'bg-gray-200 dark:bg-slate-800 text-gray-600 dark:text-slate-300',
        text: type || t('wallet.transactionDetail.type.walletActivity'),
      };
  }
};

export default function TransactionDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const location = useLocation();
  const { transactions } = useWalletData(true);
  const { user } = usePublicAuth();
  const [transaction, setTransaction] = useState(location.state?.transaction);
  const [requestInfo, setRequestInfo] = useState(null);
  const [otherPartyName, setOtherPartyName] = useState(null);
  const [loadingOtherParty, setLoadingOtherParty] = useState(false);
  const [loading, setLoading] = useState(!location.state?.transaction);

  useEffect(() => {
    const existing = transactions.find(tx => tx.id === transactionId);
    if (existing) {
      setTransaction(existing);
      setLoading(false);
    }
  }, [transactions, transactionId]);

  useEffect(() => {
    const fetchTransaction = async () => {
      if (transaction || !transactionId) return;
      try {
        const docRef = doc(
          db,
          'wallet_transactions',
          location.state?.userId || 'unknown',
          'transactions',
          transactionId,
        );
        const snapshot = await getDoc(docRef);
        if (snapshot.exists()) {
          setTransaction({
            id: snapshot.id,
            ...snapshot.data(),
          });
        }
      } catch (error) {
        console.error('Failed to load transaction:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTransaction();
  }, [transaction, transactionId, location.state?.userId]);

  useEffect(() => {
    const fetchRequestInfo = async () => {
      if (!transaction?.requestId || !user?.uid) {
        setRequestInfo(null);
        setOtherPartyName(null);
        setLoadingOtherParty(false);
        return;
      }

      setLoadingOtherParty(true);
      try {
        console.log('🔍 TransactionDetailPage - Starting fetch:', {
          requestId: transaction.requestId,
          transactionType: transaction.type,
        });

        let requestData = null;
        let isRideRequest = false;

        // CRITICAL: Check ride_requests FIRST for ride transactions
        // Transaction description contains "Ride:" for ride transactions
        const isRideTransaction = transaction.description?.includes('Ride:');

        if (isRideTransaction) {
          console.log(
            '🔍 Checking ride_requests collection first (ride transaction detected)',
          );
          const rideRequestDoc = await getDoc(
            doc(db, 'ride_requests', transaction.requestId),
          );

          if (rideRequestDoc.exists()) {
            requestData = rideRequestDoc.data();
            isRideRequest = true;
            console.log('✅ Found in ride_requests collection:', {
              applicantId: requestData.applicantId,
              applicantName: requestData.applicantName,
              listingOwnerId: requestData.listingOwnerId,
              listingOwnerName: requestData.listingOwnerName,
            });
          } else {
            console.log('⚠️ Not found in ride_requests, trying requests...');
            // Fallback to requests collection
            const requestDoc = await getDoc(
              doc(db, 'requests', transaction.requestId),
            );

            if (requestDoc.exists()) {
              requestData = requestDoc.data();
              isRideRequest = false;
              console.log('✅ Found in requests collection');
            }
          }
        } else {
          // For non-ride transactions, check requests first
          const requestDoc = await getDoc(
            doc(db, 'requests', transaction.requestId),
          );

          if (requestDoc.exists()) {
            requestData = requestDoc.data();
            isRideRequest = false;
            console.log('✅ Found in requests collection');
          } else {
            // Fallback to ride_requests
            console.log('⚠️ Not found in requests, trying ride_requests...');
            const rideRequestDoc = await getDoc(
              doc(db, 'ride_requests', transaction.requestId),
            );

            if (rideRequestDoc.exists()) {
              requestData = rideRequestDoc.data();
              isRideRequest = true;
              console.log('✅ Found in ride_requests collection');
            }
          }
        }

        if (!requestData) {
          console.log('❌ No request data found');
              setRequestInfo(null);
              setOtherPartyName(t('common.unknownUser'));
              setLoadingOtherParty(false);
              return;
        }

        setRequestInfo(requestData);

        let otherPartyId = null;
        let otherPartyNameFromRequest = null;

        if (isRideRequest) {
          // Handle ride_requests: use applicantId/applicantName and listingOwnerId/listingOwnerName
          const isCurrentUserApplicant =
            requestData.applicantId === user.uid;
          const isCurrentUserListingOwner =
            requestData.listingOwnerId === user.uid;

          console.log('🔍 Ride request data:', {
            applicantId: requestData.applicantId,
            applicantName: requestData.applicantName,
            listingOwnerId: requestData.listingOwnerId,
            listingOwnerName: requestData.listingOwnerName,
            transactionType: transaction.type,
            currentUserId: user.uid,
            isCurrentUserApplicant,
            isCurrentUserListingOwner,
          });

          // Determine other party based on who current user is
          if (isCurrentUserApplicant) {
            // Current user is applicant, other party is listingOwner
            otherPartyId = requestData.listingOwnerId;
            otherPartyNameFromRequest = requestData.listingOwnerName;
          } else if (isCurrentUserListingOwner) {
            // Current user is listingOwner, other party is applicant
            otherPartyId = requestData.applicantId;
            otherPartyNameFromRequest = requestData.applicantName;
          } else {
            // Fallback: use transaction type
            if (transaction.type === 'earning') {
              // User earned money, other party is applicant (who paid)
              otherPartyId = requestData.applicantId;
              otherPartyNameFromRequest = requestData.applicantName;
            } else if (transaction.type === 'payment') {
              // User paid, other party is listingOwner (who received)
              otherPartyId = requestData.listingOwnerId;
              otherPartyNameFromRequest = requestData.listingOwnerName;
            } else {
              // Final fallback: get the other ID
              if (
                requestData.applicantId &&
                requestData.applicantId !== user.uid
              ) {
                otherPartyId = requestData.applicantId;
                otherPartyNameFromRequest = requestData.applicantName;
              } else if (
                requestData.listingOwnerId &&
                requestData.listingOwnerId !== user.uid
              ) {
                otherPartyId = requestData.listingOwnerId;
                otherPartyNameFromRequest = requestData.listingOwnerName;
              }
            }
          }

          console.log('🔍 After ride request logic:', {
            otherPartyId,
            otherPartyNameFromRequest,
            isCurrentUserApplicant,
            isCurrentUserListingOwner,
          });
        } else {
          // Handle regular requests
          const isCurrentUserRequester =
            requestData.requesterId === user.uid;
          const isCurrentUserTripOwner =
            requestData.tripOwnerId === user.uid;

          if (isCurrentUserRequester && requestData.tripOwnerId) {
            otherPartyId = requestData.tripOwnerId;
            otherPartyNameFromRequest = requestData.tripOwnerName;
          } else if (isCurrentUserTripOwner && requestData.requesterId) {
            otherPartyId = requestData.requesterId;
            otherPartyNameFromRequest = requestData.requesterName;
          } else if (transaction.type === 'earning') {
            otherPartyId = requestData.requesterId;
            otherPartyNameFromRequest = requestData.requesterName;
          } else if (transaction.type === 'payment') {
            otherPartyId = requestData.tripOwnerId;
            otherPartyNameFromRequest = requestData.tripOwnerName;
          }

          if (!otherPartyId) {
            if (
              requestData.requesterId &&
              requestData.requesterId !== user.uid
            ) {
              otherPartyId = requestData.requesterId;
              otherPartyNameFromRequest = requestData.requesterName;
            } else if (
              requestData.tripOwnerId &&
              requestData.tripOwnerId !== user.uid
            ) {
              otherPartyId = requestData.tripOwnerId;
              otherPartyNameFromRequest = requestData.tripOwnerName;
            }
          }
        }

        // Avoid showing the current user's own name as the other party
        if (otherPartyId && otherPartyId === user.uid && isRideRequest) {
          console.log(
            '⚠️ Logic error detected: otherPartyId matches currentUser, fixing...',
          );
          const isCurrentUserApplicant =
            requestData.applicantId === user.uid;
          const isCurrentUserListingOwner =
            requestData.listingOwnerId === user.uid;

          if (isCurrentUserApplicant) {
            // User is applicant, other party MUST be listingOwner
            otherPartyId = requestData.listingOwnerId;
            otherPartyNameFromRequest = requestData.listingOwnerName;
            console.log('🔧 Fixed: user is applicant, other party is listingOwner:', {
              otherPartyId,
              otherPartyNameFromRequest,
            });
          } else if (isCurrentUserListingOwner) {
            // User is listingOwner, other party MUST be applicant
            otherPartyId = requestData.applicantId;
            otherPartyNameFromRequest = requestData.applicantName;
            console.log('🔧 Fixed: user is listingOwner, other party is applicant:', {
              otherPartyId,
              otherPartyNameFromRequest,
            });
          } else {
            console.log('⚠️ Cannot determine other party, nullifying');
            otherPartyNameFromRequest = null;
          }
        }

        // First try to use name from request document
        console.log('🔍 Final check before setting name:', {
          otherPartyNameFromRequest,
          otherPartyId,
          isRideRequest,
        });

        if (otherPartyNameFromRequest) {
          console.log('✅ Using name from request:', otherPartyNameFromRequest);
          setOtherPartyName(otherPartyNameFromRequest);
          setLoadingOtherParty(false);
          return;
        }

        // If name not in request, fetch from user document
        if (otherPartyId && otherPartyId !== user.uid) {
          console.log('🔍 Fetching from users collection:', otherPartyId);
          try {
            const otherPartyDoc = await getDoc(
              doc(db, 'users', otherPartyId),
            );

            if (otherPartyDoc.exists()) {
              const otherPartyData = otherPartyDoc.data();
              const name =
                otherPartyData.displayName ||
                `${otherPartyData.firstName || ''} ${
                  otherPartyData.lastName || ''
                }`.trim() ||
                otherPartyData.email?.split('@')[0] ||
                null;

              if (name) {
                setOtherPartyName(name);
              } else {
                const emailName = otherPartyData.email?.split('@')[0];
                setOtherPartyName(emailName || t('common.unknownUser'));
              }
            } else {
              setOtherPartyName(t('common.unknownUser'));
            }
          } catch (userError) {
            setOtherPartyName(t('common.unknownUser'));
          }
        } else {
          setOtherPartyName(t('common.unknownUser'));
        }
      } catch (error) {
        console.error('❌ Error fetching other party info:', error);
        setOtherPartyName(t('common.unknownUser'));
      } finally {
        setLoadingOtherParty(false);
      }
    };
    fetchRequestInfo();
  }, [
    transaction?.requestId,
    transaction?.type,
    transaction?.description,
    user?.uid,
  ]);

  const detailRows = useMemo(() => {
    if (!transaction) return [];
    const shouldShowOtherParty =
      transaction.requestId &&
      (transaction.type === 'earning' || transaction.type === 'payment');

    return [
      {
        label: t('wallet.transactionDetail.detail.description'),
        value:
          transaction.description || t('wallet.transactionDetail.detail.walletActivity'),
        icon: FileText,
      },
      {
        label: t('wallet.transactionDetail.detail.status'),
        value: transaction.status || t('wallet.statusCompleted'),
        icon: CreditCard,
      },
      {
        label: t('wallet.transactionDetail.detail.date'),
        value: formatDate(transaction.date, t),
        icon: CalendarDays,
      },
      shouldShowOtherParty && {
        label:
          transaction.type === 'earning'
            ? t('wallet.transactionDetail.detail.from')
            : t('wallet.transactionDetail.detail.to'),
        value: loadingOtherParty
          ? t('common.loading')
          : otherPartyName || t('common.unknownUser'),
        icon: User,
      },
      transaction.requestId && {
        label: t('wallet.transactionDetail.detail.requestId'),
        value: transaction.requestId,
        icon: FileText,
      },
      transaction.paymentMethod && {
        label: t('wallet.transactionDetail.detail.paymentMethod'),
        value: transaction.paymentMethod,
        icon: CreditCard,
      },
      transaction.withdrawalMethod && {
        label: t('wallet.transactionDetail.detail.withdrawalMethod'),
        value: transaction.withdrawalMethod,
        icon: Banknote,
      },
      transaction.tip && transaction.tip > 0 && {
        label: t('wallet.transactionDetail.detail.tip'),
        value: `CAD ${parseFloat(transaction.tip).toFixed(2)}`,
        icon: Banknote,
      },
    ].filter(Boolean);
  }, [transaction, otherPartyName, loadingOtherParty, t]);

  const typeStyles = getTypeStyles(transaction?.type, t);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      <section className="pt-32 pb-8">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/wallet/all-transactions')}
            className="inline-flex items-center gap-2 text-gray-600 dark:text-slate-400 text-sm font-semibold mb-6 hover:text-gray-900 dark:hover:text-white transition"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('wallet.transactionDetail.backToTransactions')}
          </button>
          {loading ? (
            <div className="rounded-3xl bg-white dark:bg-slate-900 p-12 text-center shadow-lg border border-gray-100 dark:border-slate-800 text-gray-900 dark:text-white">
              {t('wallet.transactionDetail.loading')}
            </div>
          ) : !transaction ? (
            <div className="rounded-3xl bg-white dark:bg-slate-900 p-12 text-center shadow-lg border border-gray-100 dark:border-slate-800 text-gray-900 dark:text-white">
              {t('wallet.transactionDetail.notFound')}
            </div>
          ) : (
            <div className="space-y-8">
              <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg border border-gray-100 dark:border-slate-800">
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className={`h-14 w-14 rounded-2xl flex items-center justify-center ${typeStyles.classes}`}
                  >
                    <typeStyles.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-widest text-gray-500 dark:text-slate-400">
                      {typeStyles.text}
                    </p>
                    <p className="text-4xl font-black text-gray-900 dark:text-white">
                      {formatCurrency(transaction.amount || 0)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-full bg-gray-100 dark:bg-slate-800 px-4 py-2 text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wide inline-flex">
                  {transaction.status || t('wallet.statusCompleted')}
                </div>
              </div>

              <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg border border-gray-100 dark:border-slate-800 space-y-6">
                <h2 className="text-xl font-black text-gray-900 dark:text-white">
                  {t('wallet.transactionDetail.title')}
                </h2>
                <div className="space-y-4">
                  {detailRows.map(row => (
                    <div
                      key={row.label}
                      className="flex items-start justify-between gap-4 border-b border-gray-100 dark:border-slate-800 pb-4 last:border-none last:pb-0"
                    >
                      <div className="flex items-center gap-3">
                        <row.icon className="h-5 w-5 text-gray-400 dark:text-slate-500" />
                        <span className="text-sm font-semibold text-gray-500 dark:text-slate-400">
                          {row.label}
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white text-right">
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
